<script setup lang="ts">
/**
 * Quick-add overlay container (E14).
 *
 * Mounted once, globally, in AppShell. Listens for the 'n' keyboard shortcut
 * and for explicit calls to the quickAddStore from anywhere. Renders nothing
 * when closed; when open, shows either the picker step or the form for the
 * currently-selected type.
 *
 * Role gate: the 'n' shortcut only fires for analyst/admin/super_admin.
 * Executive users can't open the overlay at all — they're read-only per
 * E13 §1 resolution #4.
 */
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuickAddStore } from '@/stores/quickAddStore';
import { useAuthStore } from '@/stores/authStore';
import { useKeyboardShortcut } from '@/composables/useKeyboardShortcut';
import { useEscapeKey } from '@/composables/useEscapeKey';
import QuickAddPicker from './QuickAddPicker.vue';
import QuickAddIocForm from './QuickAddIocForm.vue';
import QuickAddActorForm from './QuickAddActorForm.vue';
import QuickAddCampaignForm from './QuickAddCampaignForm.vue';
import QuickAddNoteForm from './QuickAddNoteForm.vue';

const qa = useQuickAddStore();
const auth = useAuthStore();

// storeToRefs unwraps Pinia's proxy so we get a real Ref<boolean> back that
// useEscapeKey can watch. Accessing qa.open directly loses the reactive ref.
const { open: qaOpen, selectedType } = storeToRefs(qa);

const canCreate = computed(() => {
  const role = auth.user?.role;
  return role === 'analyst' || role === 'admin' || role === 'super_admin';
});

// 'n' (new) from anywhere in the app opens the picker, unless focus is in
// a form input. Silently ignored for executives and unauthenticated users.
useKeyboardShortcut({
  key: 'n',
  handler: () => {
    if (canCreate.value && !qaOpen.value) {
      qa.openPicker();
    }
  },
});

// Escape closes the overlay whenever it's open.
useEscapeKey(qaOpen, () => qa.close());

// Click-outside handler: the parent dimmer div swallows clicks. Only clicks
// directly on the dimmer (not inside the card) should close.
function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) {
    qa.close();
  }
}
</script>

<template>
  <div
    v-if="qaOpen"
    class="quick-add-overlay fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/60"
    data-qa-overlay
    @click="onBackdropClick"
  >
    <div
      class="quick-add-overlay-card bg-surface-800 rounded-xl p-5 w-full max-w-lg flex flex-col gap-4 shadow-2xl border border-border-default"
      data-qa-card
    >
      <QuickAddPicker
        v-if="!selectedType"
        @select="(t) => qa.openFor(t)"
        @cancel="qa.close"
      />
      <QuickAddNoteForm
        v-else-if="selectedType === 'note'"
        @done="qa.close"
      />
      <QuickAddIocForm
        v-else-if="selectedType === 'indicator'"
        @done="qa.close"
      />
      <QuickAddActorForm
        v-else-if="selectedType === 'actor'"
        @done="qa.close"
      />
      <QuickAddCampaignForm
        v-else-if="selectedType === 'campaign'"
        @done="qa.close"
      />
      <div v-else-if="selectedType === 'rule'" class="flex flex-col gap-3">
        <h2 class="text-lg font-semibold text-text-primary">Add Detection Rule</h2>
        <p class="text-sm text-text-muted">
          Detection rules need full SPL syntax and technique mapping — no useful
          quick form. Please use the Detection Rules admin view for new rules.
        </p>
        <div class="flex items-center justify-end">
          <button class="qa-btn-ghost" @click="qa.close">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qa-btn-ghost {
  color: var(--color-text-secondary, #a0a0aa);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}
.qa-btn-ghost:hover {
  background: var(--color-surface-700, #2a2a3e);
}
</style>
